/**
 * ISO-3166 alpha-2 country code → flag emoji.
 * Uses the Unicode regional-indicator block: each ASCII letter maps to
 * a codepoint 127397 higher, so "US" → 🇺🇸. Returns '' for invalid input.
 */
export function flagFromCode(code) {
  if (!code || code.length !== 2) return '';
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0)));
}

// ISO-3166 alpha-2 country codes with display names.
// Kept minimal and hand-sorted so common countries surface first.
// The order determines the option order in the picker.
export const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  // Europe
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IS', name: 'Iceland' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'GR', name: 'Greece' },
  { code: 'RO', name: 'Romania' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'TR', name: 'Türkiye' },
  // LATAM
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'PR', name: 'Puerto Rico' },
  // Asia
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'PH', name: 'Philippines' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IN', name: 'India' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'NP', name: 'Nepal' },
  { code: 'IL', name: 'Israel' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  // Africa
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' },
  { code: 'EG', name: 'Egypt' },
  { code: 'MA', name: 'Morocco' },
  // Fallback
  { code: 'OTHER', name: 'Other' },
];

/** Look up a country by ISO code. Returns null when unknown. */
export function countryByCode(code) {
  if (!code) return null;
  const c = String(code).toUpperCase();
  return COUNTRIES.find((x) => x.code === c) || null;
}

/** Look up a country by human-readable name (case-insensitive). */
export function countryByName(name) {
  if (!name) return null;
  const n = String(name).toLowerCase().trim();
  return COUNTRIES.find((x) => x.name.toLowerCase() === n) || null;
}

/**
 * Best-effort browser locale → default country guess.
 * navigator.language returns strings like "en-US" / "pt-BR" — we pull the
 * region suffix and fall back to `null` when no match. Used to preseed
 * the picker so the majority of users don't have to change it.
 */
export function guessCountryFromLocale() {
  try {
    const lang = navigator.language || '';
    const region = lang.split('-')[1];
    if (!region) return null;
    return countryByCode(region.toUpperCase()) || null;
  } catch { return null; }
}
